'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, 
  Server, 
  Activity, 
  AlertTriangle, 
  Settings, 
  FileText, 
  Users,
  Menu,
  ChevronLeft,
  ChevronRight,
  Network
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  badge?: number;
}

const navItems: NavItem[] = [
  { id: 'dashboard', label: '仪表板', icon: LayoutDashboard },
  { id: 'devices', label: '设备管理', icon: Server },
  { id: 'monitoring', label: '实时监控', icon: Activity },
  { id: 'alerts', label: '告警中心', icon: AlertTriangle, badge: 3 },
  { id: 'topology', label: '网络拓扑', icon: Network },
  { id: 'logs', label: '系统日志', icon: FileText },
  { id: 'users', label: '用户管理', icon: Users },
  { id: 'settings', label: '系统设置', icon: Settings },
];

interface SidebarProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
}

export default function Sidebar({ activeTab, onTabChange }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <motion.aside
      initial={{ width: 256 }}
      animate={{ width: collapsed ? 72 : 256 }}
      transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
      className={cn(
        "fixed left-0 top-0 h-screen bg-[var(--sidebar)] border-r border-[var(--sidebar-border)] z-40 flex flex-col"
      )}
    >
      {/* Logo区域 */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-[var(--sidebar-border)]">
        <motion.div 
          className="flex items-center gap-3 overflow-hidden"
          animate={{ opacity: collapsed ? 0 : 1 }}
        >
          <div className="w-8 h-8 rounded-lg bg-[var(--primary)] flex items-center justify-center flex-shrink-0">
            <Network className="w-5 h-5 text-white" />
          </div>
          {!collapsed && (
            <span className="font-semibold text-[var(--sidebar-foreground)] whitespace-nowrap">
              NetManager
            </span>
          )}
        </motion.div>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-1.5 rounded-lg hover:bg-[var(--sidebar-accent)] text-[var(--sidebar-foreground)] transition-colors"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      {/* 导航菜单 */}
      <nav className="flex-1 py-4 px-3 overflow-y-auto">
        <ul className="space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            
            return (
              <li key={item.id}>
                <button
                  onClick={() => onTabChange(item.id)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group relative",
                    isActive 
                      ? "bg-[var(--primary)] text-white shadow-sm" 
                      : "text-[var(--sidebar-foreground)] hover:bg-[var(--sidebar-accent)] hover:text-[var(--sidebar-accent-foreground)]"
                  )}
                >
                  <Icon className={cn(
                    "w-5 h-5 flex-shrink-0",
                    isActive ? "text-white" : "text-[var(--sidebar-foreground)] group-hover:text-[var(--sidebar-accent-foreground)]"
                  )} />
                  
                  {!collapsed && (
                    <span className="font-medium text-sm whitespace-nowrap flex-1 text-left">
                      {item.label}
                    </span>
                  )}
                  
                  {!collapsed && item.badge && (
                    <span className={cn(
                      "px-2 py-0.5 text-xs font-medium rounded-full",
                      isActive 
                        ? "bg-white/20 text-white" 
                        : "bg-[var(--destructive)] text-white"
                    )}>
                      {item.badge}
                    </span>
                  )}

                  {/* 折叠状态下的tooltip */}
                  {collapsed && (
                    <div className="absolute left-full ml-2 px-2 py-1 bg-[var(--foreground)] text-white text-xs rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50">
                      {item.label}
                      {item.badge && (
                        <span className="ml-2 px-1.5 py-0.5 bg-[var(--destructive)] rounded-full">
                          {item.badge}
                        </span>
                      )}
                    </div>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* 底部信息 */}
      <div className="p-4 border-t border-[var(--sidebar-border)]">
        <div className={cn(
          "flex items-center gap-3",
          collapsed && "justify-center"
        )}>
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[var(--primary)] to-cyan-400 flex items-center justify-center flex-shrink-0">
            <span className="text-white text-xs font-semibold">管</span>
          </div>
          {!collapsed && (
            <div className="overflow-hidden">
              <p className="text-sm font-medium text-[var(--foreground)] truncate">系统管理员</p>
              <p className="text-xs text-[var(--muted-foreground)] truncate">admin@netmanager.com</p>
            </div>
          )}
        </div>
      </div>
    </motion.aside>
  );
}
